import React, { useState } from 'react';
import { ChevronRight, Plus } from 'lucide-react';
import { NavArrowRight } from 'iconoir-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { Icon, IconName } from '../menu-icon/menu-icon';
import { cn } from 'lib/utils';
import { sidebarSectionHeaderLabelClass } from './sidebar-section-label';

interface SidebarSectionProps {
  title: string;
  icon?: IconName;
  children: React.ReactNode;
  onAddClick?: (e: React.MouseEvent) => void;
  defaultOpen?: boolean;
  variant?: 'default' | 'figma';
}

export function SidebarCollapsibleSection({
  title,
  icon,
  children,
  onAddClick,
  defaultOpen = false,
  variant = 'default',
}: SidebarSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const isFigma = variant === 'figma';

  if (isFigma) {
    return (
      <Collapsible
        open={isOpen}
        onOpenChange={setIsOpen}
        className="group/section w-full"
      >
        <div className="flex w-full items-center justify-between gap-2">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex min-w-0 flex-1 cursor-pointer items-center gap-0.5 text-left group-data-[collapsible=icon]:justify-center min-h-5"
            >
              <span className={cn(sidebarSectionHeaderLabelClass, 'group-data-[collapsible=icon]:hidden')}>
                {title}
              </span>
              <NavArrowRight
                width={16}
                height={16}
                strokeWidth={1.75}
                className={cn(
                  'shrink-0 text-[#888888] transition-transform duration-200 dark:text-true-white group-data-[collapsible=icon]:hidden',
                  isOpen ? '-rotate-90' : 'rotate-90'
                )}
              />
            </button>
          </CollapsibleTrigger>

          {onAddClick && (
            <button
              type="button"
              onClick={onAddClick}
              className="cursor-pointer rounded-md p-1 text-[#888888] opacity-0 transition-opacity hover:bg-[hsl(var(--grey-100))] group-hover/section:opacity-100 group-data-[collapsible=icon]:hidden dark:text-[hsl(var(--grey-400))]"
              aria-label={`Add ${title}`}
            >
              <Plus size={16} strokeWidth={1.75} />
            </button>
          )}
        </div>

        <CollapsibleContent className="flex flex-col gap-1 data-[state=open]:pt-1 group-data-[collapsible=icon]:hidden">
          {children}
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="group/section w-full">
      <div className="flex w-full items-center justify-between gap-3 px-4 py-1.5 group-data-[collapsible=icon]:justify-center">
        <CollapsibleTrigger asChild>
          <button className="flex flex-1 cursor-pointer items-center gap-3 text-left text-[13px] font-medium leading-[22px] text-sidebar-foreground hover:text-foreground group-data-[collapsible=icon]:justify-center">
            {icon && <Icon name={icon} size={16} />}
            <span className="group-data-[collapsible=icon]:hidden">{title}</span>
            <ChevronRight
              className={cn(
                'h-3 w-3 transition-transform duration-200 group-data-[collapsible=icon]:hidden',
                isOpen && 'rotate-90'
              )}
            />
          </button>
        </CollapsibleTrigger>

        {onAddClick && (
          <button
            type="button"
            onClick={onAddClick}
            className="cursor-pointer rounded-md p-1 text-muted-foreground transition-opacity hover:bg-muted-foreground/10 hover:text-foreground group-data-[collapsible=icon]:hidden"
          >
            <Plus size={18} />
          </button>
        )}
      </div>

      <CollapsibleContent className="space-y-1 group-data-[collapsible=icon]:hidden">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}
